/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The effective default discovery mode. Unconfigured connections follow this value.
 */
export const DiscoveryMode = {
  Progressive: "progressive",
  Direct: "direct",
} as const;
/**
 * The effective default discovery mode. Unconfigured connections follow this value.
 */
export type DiscoveryMode = ClosedEnum<typeof DiscoveryMode>;

/**
 * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
 */
export const MetaMcpServerNetworkAccessMode = {
  PublicOnly: "public_only",
  Dual: "dual",
  PrivateOnly: "private_only",
} as const;
/**
 * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
 */
export type MetaMcpServerNetworkAccessMode = ClosedEnum<
  typeof MetaMcpServerNetworkAccessMode
>;

/**
 * The visibility of a meta MCP server. Disabled refuses traffic; private requires a user session.
 */
export const MetaMcpServerVisibility = {
  Disabled: "disabled",
  Private: "private",
} as const;
/**
 * The visibility of a meta MCP server. Disabled refuses traffic; private requires a user session.
 */
export type MetaMcpServerVisibility = ClosedEnum<
  typeof MetaMcpServerVisibility
>;

/**
 * A meta MCP server: an aggregate server fronting an explicitly managed set of MCP servers. URL addressability lives on its MCP endpoints.
 */
export type MetaMcpServer = {
  /**
   * When the meta MCP server was created
   */
  createdAt: Date;
  /**
   * The effective default discovery mode. Unconfigured connections follow this value.
   */
  discoveryMode: DiscoveryMode;
  /**
   * The ID of the meta MCP server
   */
  id: string;
  /**
   * Operator-authored server instructions returned in the gateway's MCP initialize response. Null when the gateway serves Speakeasy's built-in instructions.
   */
  instructions?: string | undefined;
  /**
   * The number of live members. Only populated by listMetaMcpServers.
   */
  memberCount?: number | undefined;
  /**
   * The display name of the meta MCP server
   */
  name: string;
  /**
   * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
   */
  networkAccessMode: MetaMcpServerNetworkAccessMode;
  /**
   * The organization this meta MCP server belongs to
   */
  organizationId: string;
  /**
   * The project this meta MCP server belongs to
   */
  projectId: string;
  /**
   * When the meta MCP server was last updated
   */
  updatedAt: Date;
  /**
   * The ID of the user session issuer used to authenticate callers. Null when no issuer is attached.
   */
  userSessionIssuerId?: string | undefined;
  /**
   * The visibility of a meta MCP server. Disabled refuses traffic; private requires a user session.
   */
  visibility: MetaMcpServerVisibility;
};

/** @internal */
export const DiscoveryMode$inboundSchema: z.ZodMiniEnum<typeof DiscoveryMode> =
  z.enum(DiscoveryMode);

/** @internal */
export const MetaMcpServerNetworkAccessMode$inboundSchema: z.ZodMiniEnum<
  typeof MetaMcpServerNetworkAccessMode
> = z.enum(MetaMcpServerNetworkAccessMode);

/** @internal */
export const MetaMcpServerVisibility$inboundSchema: z.ZodMiniEnum<
  typeof MetaMcpServerVisibility
> = z.enum(MetaMcpServerVisibility);

/** @internal */
export const MetaMcpServer$inboundSchema: z.ZodMiniType<
  MetaMcpServer,
  unknown
> = z.pipe(
  z.object({
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    discovery_mode: DiscoveryMode$inboundSchema,
    id: z.string(),
    instructions: z.optional(z.string()),
    member_count: z.optional(z.int()),
    name: z.string(),
    network_access_mode: MetaMcpServerNetworkAccessMode$inboundSchema,
    organization_id: z.string(),
    project_id: z.string(),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    user_session_issuer_id: z.optional(z.string()),
    visibility: MetaMcpServerVisibility$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "created_at": "createdAt",
      "discovery_mode": "discoveryMode",
      "member_count": "memberCount",
      "network_access_mode": "networkAccessMode",
      "organization_id": "organizationId",
      "project_id": "projectId",
      "updated_at": "updatedAt",
      "user_session_issuer_id": "userSessionIssuerId",
    });
  }),
);

export function metaMcpServerFromJSON(
  jsonString: string,
): SafeParseResult<MetaMcpServer, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => MetaMcpServer$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'MetaMcpServer' from JSON`,
  );
}
